import React, { useEffect, useRef, useState } from "react";
import { AiOutlineCloseCircle } from "react-icons/ai";
import Modal from './Modal'
import { AlertTriangle } from "lucide-react";
import { API_BASE } from '../config'

export function UpdateDeviceStatusModal({ isOpen, onClose, onSubmit, device, userCommunity }) {
  const modalRef = useRef(null);

  const [status, setStatus] = useState("operational");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);

  // Update form data when device changes
  useEffect(() => {
    if (device && isOpen) {
      setStatus((device.status || "operational").toLowerCase());
      setDetails("");
    }
  }, [device, isOpen]);

  /* ---------------- ESC key close ---------------- */
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  /* ---------------- Click outside close ---------------- */
  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!isOpen || !device) return null;

  const getStatusColor = (statusValue) => {
    switch (statusValue) {
      case "operational":
        return "bg-green-100 border-green-300 text-green-800";
      case "maintenance":
        return "bg-yellow-100 border-yellow-300 text-yellow-800";
      case "offline":
        return "bg-red-100 border-red-300 text-red-800";
      default:
        return "";
    }
  };

  /* ---------------- Submit ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const token = sessionStorage.getItem('id_token');

      const decodeIdTokenUser = () => {
        try {
          if (!token) return null;
          const payload = token.split('.')[1];
          const json = decodeURIComponent(atob(payload.replace(/-/g, '+').replace(/_/g, '/')).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          }).join(''));
          return JSON.parse(json);
        } catch (e) {
          return null;
        }
      };
      const tokenUser = decodeIdTokenUser() || {};
      const updatedBy = (sessionStorage.getItem('user_email') || tokenUser.email || tokenUser.preferred_username || '').toString().toLowerCase() || 'admin';

      const payload = {
        COMMUNITY_ID: userCommunity || sessionStorage.getItem('community_id') || device.communityId || device.COMMUNITY_ID,
        DEVICE_ID: device.id,
        STATUS: status,
        UPDATED_BY: updatedBy,
      };

      console.log("Submitting device update payload:", payload);

      const response = await fetch(`${API_BASE}/devices`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.text();
        throw new Error(`Failed to update device: ${errData}`);
      }

      const result = await response.json();
      console.log("Device updated:", result);
      onSubmit(result);
      onClose();
    } catch (error) {
      console.error("Error updating device:", error);
      alert("Unable to update device. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Update Device Status" onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-4">
          <div className="space-y-4">
            {/* Device Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h3 className="mb-1">{device.name}</h3>
              <p className="text-sm text-gray-600">
                Type: {device.type.charAt(0).toUpperCase() + device.type.slice(1)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Last Updated: {new Date(device.lastUpdated).toLocaleString()}
              </p>
            </div>

            {/* Current Status */}
            <div>
              <label className="block text-sm mb-2 text-gray-700">Current Status</label>
              <div className={`px-3 py-2 border-2 rounded-lg ${getStatusColor(device.status)}`}>
                {device.status.charAt(0).toUpperCase() + device.status.slice(1)}
              </div>
            </div>

            {/* New Status */}
            <div>
              <label className="block text-sm mb-2 text-gray-700">Update Status To *</label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setStatus('operational')}
                  disabled={loading}
                  className={`w-full px-4 py-3 border-2 rounded-lg text-left transition-all ${
                    status === 'operational'
                      ? 'bg-green-100 border-green-400 text-green-800'
                      : 'border-gray-300 text-gray-700 hover:border-green-300'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      status === 'operational' ? 'bg-green-600 border-green-600' : 'border-gray-300'
                    }`}>
                      {status === 'operational' && (
                        <div className="w-full h-full flex items-center justify-center text-white text-xs">✓</div>
                      )}
                    </div>
                    <div>
                      <div>Operational</div>
                      <div className="text-xs opacity-70">Device is working normally</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setStatus('maintenance')}
                  disabled={loading}
                  className={`w-full px-4 py-3 border-2 rounded-lg text-left transition-all ${
                    status === 'maintenance'
                      ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
                      : 'border-gray-300 text-gray-700 hover:border-yellow-300'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      status === 'maintenance' ? 'bg-yellow-600 border-yellow-600' : 'border-gray-300'
                    }`}>
                      {status === 'maintenance' && (
                        <div className="w-full h-full flex items-center justify-center text-white text-xs">✓</div>
                      )}
                    </div>
                    <div>
                      <div>Maintenance</div>
                      <div className="text-xs opacity-70">Device is under maintenance</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setStatus('offline')}
                  disabled={loading}
                  className={`w-full px-4 py-3 border-2 rounded-lg text-left transition-all ${
                    status === 'offline'
                      ? 'bg-red-100 border-red-400 text-red-800'
                      : 'border-gray-300 text-gray-700 hover:border-red-300'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      status === 'offline' ? 'bg-red-600 border-red-600' : 'border-gray-300'
                    }`}>
                      {status === 'offline' && (
                        <div className="w-full h-full flex items-center justify-center text-white text-xs">✓</div>
                      )}
                    </div>
                    <div>
                      <div>Offline</div>
                      <div className="text-xs opacity-70">Device is not working</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Status Details */}
            <div>
              <label className="block text-sm mb-1 text-gray-700">Status Details</label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={3}
                placeholder="Add notes about the status change..."
                disabled={loading}
              />
            </div>

            {/* Warning for offline status */}
            {status === 'offline' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                <AlertTriangle size={20} className="text-red-600 flex-shrink-0" />
                <div>
                  <p className="text-sm text-red-800">
                    <strong>Warning:</strong> Setting status to offline will alert residents about the device outage.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className={`flex-1 px-4 py-2 rounded-lg text-white ${
                loading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {loading ? "Updating..." : "Update Status"}
            </button>
          </div>
          </form>
    </Modal>
  );
}

export default UpdateDeviceStatusModal;
